import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import {
  Box,
  Grid,
  InputAdornment,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import useUsers from 'api/useUsers';
import { AxiosError } from 'axios';
import { ETaskId } from 'common/contracts';
import { EFeatureFlag } from 'constants/enums';
import { Decimal } from 'decimal.js';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';

import AcTooltip from 'components/AcTooltip/AcTooltip';
import FormHeader from 'components/FormHeader/FormHeader';

import usePricing from '../../api/usePricing';
import { MAX_USD_PRICE, MIN_USD_PRICE } from '../../constants/constants';
import { errorResponse } from '../../utils/errorsTextHelper';
import { permissionsUtil } from '../../utils/permissionsUtil';
import { EToasterStatus, showToast } from '../../utils/showToast';
import AcCard from '../AcCard/AcCard';
import AcInput from '../AcInput/AcInput';
import DataTable from '../DataTable/DataTable';

import { AVAILABLE_COUNTRIES } from './Pricing.constants';
import { CustomPricing, PricingMeta } from './Pricing.types';
import PricingTableInputCell from './PricingTableInputCell';

enum TAX_TYPE {
  TAX_INCLUDED = 'taxIncluded',
  TAX_EXCLUDED = 'taxExcluded'
}

const PricingForm = () => {
  const isManuallyUpdatedListFilled = useRef(false);
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { pricePointId } = useParams();
  
  const { fetchFeatureFlags } = useUsers({ publisherId: publisherId });
  const { setTaskStatus, hasCategories, isWebStore, isWebCheckout } = useChecklistState();

  
  const {
    getPricePoint,
    getPricings,
    calculatePricing,
    updatePricing,
    addPricing
  } = usePricing(publisherId, pricePointId, true);

  const [currentPricePoint, setCurrentPricePoint] = useState<Decimal>(
    new Decimal(MIN_USD_PRICE)
  );
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isPricePointTaken, setIsPricePointTaken] = useState(false);
  const [prices, setPrices] = useState<CustomPricing[]>([]);
  const manuallyUpdatedPricesByCountryCode3 = useMemo(() => {
    if (!prices.length) return [];
    isManuallyUpdatedListFilled.current = true;
    return prices
      ?.filter((price: CustomPricing) => price.manuallyUpdated)
      .map((p: CustomPricing) => p.countryCode3);
  }, [prices]);
  const [isDirty, setIsDirty] = useState(false);

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 1000;
  const [isTyping, setIsTyping] = useState(false);

  const hasFeatureFlagPricePointAlerting =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_PRICE_POINT_ALERTING
    ];

  const checkoutIsPricePointTaken = () => {
    return getPricings.data?.result.find((pricePoint: PricingMeta) =>
      new Decimal(pricePoint.priceInCents).eq(currentPricePoint.mul(100))
    );
  };

  const handlePricePointChange = () => {
    if (checkoutIsPricePointTaken()) {
      setIsPricePointTaken(true);
      return;
    }

    if (!isManuallyUpdatedListFilled.current && pricePointId) return;
    setIsPricePointTaken(false);
    const anchors = [currentPricePoint.mul(100).toNumber()];

    calculatePricing.mutate(anchors, {
      onSuccess: (data: any) => {
        const newPrices = data.result[0].customPricing.map(
          (price: CustomPricing) => {
            if (
              manuallyUpdatedPricesByCountryCode3.includes(price.countryCode3)
            ) {
              return prices.find(
                (p: CustomPricing) => price.countryCode3 === p.countryCode3
              );
            } else return price;
          }
        );
        setPrices(newPrices);
        if (hasCategories && (isWebStore || isWebCheckout)) {
          setTaskStatus(ETaskId.CREATE_PRICE_POINTS);
        }
      },
      onError: (error) => {
        if (error instanceof AxiosError) {
          showToast({
            message: errorResponse(error),
            status: EToasterStatus.ERROR
          });
        }
      }
    });
  };

  const updateCountryPriceManually = (
    newPrice: Decimal,
    countryCode3: string
  ) => {
    const newPrices = [...prices];

    for (let i = 0; i < newPrices.length; i++) {
      if (newPrices[i].countryCode3 === countryCode3) {
        newPrices[i].price = newPrice;
        newPrices[i].manuallyUpdated = true;
        newPrices[i].fresh = false;
        break;
      }
    }
    setIsDirty(true);
    setPrices(newPrices);
  };

  const getFormattedCurrency = (params: any, getBasePrice = false): number => {
    const multiplier = params.row.multiplier;
    const customPrice = new Decimal(params.row.price)
      .div(multiplier)
      .toNumber();

    // find the rate of the currency
    let rate = params.row.rate || 1;
    rate = new Decimal(rate).mul(100).div(multiplier).toNumber();

    const priceFromPoint = currentPricePoint.mul(rate).toNumber();

    return getBasePrice || !customPrice
      ? new Decimal(params.row.price).div(multiplier).toNumber()
      : customPrice || priceFromPoint;
  };

  const formatPrices = () => {
    return prices.map((price: CustomPricing) => {
      return {
        countryCode3: price.countryCode3.toUpperCase(),
        currencyCode: price.currencyCode,
        price: new Decimal(price.price),
        manuallyUpdated: price.manuallyUpdated || false
      } as CustomPricing;
    });
  };

  const handleAddPricing = () => {
    const formattedPrices = formatPrices();
    addPricing.mutate({
      priceInCents: currentPricePoint.mul(100),
      customPricing: formattedPrices
    });
    navigate('../pricing/points');
  };

  const handleEditPricing = () => {
    if (!pricePointId) return;
    const formattedPrices = formatPrices();
    const pricingObject = {
      priceInCents: currentPricePoint.mul(100),
      customPricing: formattedPrices,
      _id: pricePointId
    };
    updatePricing.mutate(pricingObject);
    navigate('../pricing/points');
  };

  useEffect(() => {
    if (!pricePointId) {
      setCurrentPricePoint(new Decimal(MIN_USD_PRICE));
    } else if (!getPricePoint.isLoading) {
      getPricePoint.refetch();
    }
  }, [pricePointId]);

  useEffect(() => {
    if (!getPricePoint.data) return;
    setCurrentPricePoint(
      new Decimal(getPricePoint.data.result.priceInCents).div(100)
    );
    setPrices(getPricePoint.data.result.customPricing);
  }, [getPricePoint.data]);

  useEffect(() => {
    if (
      currentPricePoint &&
      currentPricePoint.greaterThanOrEqualTo(new Decimal(MIN_USD_PRICE))
    ) {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      setIsTyping(true);

      debounceTimeout.current = setTimeout(() => {
        handlePricePointChange();
        setIsTyping(false);
      }, DEBOUNCE_DELAY);

      return () => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      };
    }
  }, [currentPricePoint]);

  return (
    <div className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-scroll">
      <FormHeader
        title={pricePointId ? 'Edit Pricing' : 'New Pricing'}
        mainButtonText="Save"
        onCloseForm={() => navigate('../pricing/points')}
        onMainButtonClick={() =>
          pricePointId ? handleEditPricing() : handleAddPricing()
        }
        isFormValid={
          isDirty &&
          permissionsUtil.canUserEdit() &&
          !isTyping &&
          !currentPricePoint?.gt(MAX_USD_PRICE) &&
          !currentPricePoint?.lt(MIN_USD_PRICE) &&
          !getPricePoint.isFetching &&
          !calculatePricing.isLoading &&
          (!pricePointId ? !isPricePointTaken : true)
        }
      />
      <Stack
        sx={{
          paddingTop: '24px',
          paddingLeft: '10%',
          paddingRight: '10%'
        }}
      >
        <AcCard>
          <Typography fontFamily="'Montserrat', sans-serif" fontWeight={600}>
            USD Price Point
          </Typography>
          <Stack gap={2}>
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={5}>
                <Stack gap={2}>
                  <Grid item xs={12}>
                    <AcInput
                      value={
                        isEditingPrice
                          ? currentPricePoint
                          : currentPricePoint.toFixed(2)
                      }
                      type="number"
                      onChange={(e) => {
                        setIsDirty(true);
                        setCurrentPricePoint(new Decimal(e.target.value || 0));
                      }}
                      onFocus={() => setIsEditingPrice(true)}
                      onBlur={() => setIsEditingPrice(false)}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          )
                        },
                        htmlInput: {
                          min: MIN_USD_PRICE,
                          max: MAX_USD_PRICE
                        }
                      }}
                      error={
                        (isPricePointTaken && !pricePointId) ||
                        currentPricePoint?.lt(MIN_USD_PRICE) ||
                        currentPricePoint?.gt(MAX_USD_PRICE)
                      }
                      helperText={
                        currentPricePoint?.lt(MIN_USD_PRICE)
                          ? `Price point can't be lower than $${MIN_USD_PRICE}`
                          : currentPricePoint?.gt(MAX_USD_PRICE)
                            ? "Price point can't be higher than $5000"
                            : isPricePointTaken && !pricePointId
                              ? 'There is already a price point with this value'
                              : ''
                      }
                    />
                  </Grid>
                </Stack>
              </Grid>
            </Grid>

            <Box className="settings-card px-8">
              <DataTable
                rowIdIdentifier="countryCode3"
                columns={[
                  {
                    field: 'country',
                    headerName: 'Country',
                    disableReorder: true,
                    flex: 1,
                    renderCell: (params: any) => {
                      return (
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          gap={1}
                        >
                          <img
                            src={`./assets/flags/${(
                              AVAILABLE_COUNTRIES.find(
                                (country) =>
                                  country._id === params.id.toLowerCase()
                              )?.twoLetterId || ''
                            ).toLowerCase()}.png`}
                            alt=""
                            style={{
                              maxWidth: 16
                            }}
                          />
                          {params.value}
                        </Stack>
                      );
                    }
                  },
                  {
                    field: 'currencyCode',
                    headerName: 'Currency',
                    disableReorder: true,
                    flex: 1,
                    renderCell: (params: any) => {
                      return (
                        <Typography variant="body2" color="textSecondary">
                          {params.value}
                        </Typography>
                      );
                    }
                  },
                  {
                    field: 'price',
                    headerName: 'Price',
                    disableReorder: true,
                    flex: 1,
                    renderCell: (params: any) => {
                      return (
                        <PricingTableInputCell
                          onPriceUpdate={updateCountryPriceManually}
                          pricePoint={getFormattedCurrency(params)}
                          customPriceData={params.row}
                        />
                      );
                    },
                    sortComparator: (a, b, params1, params2) => {
                      const getPrice = (countryCode: string) => {
                        const countryPricing = prices.find(
                          (cp: CustomPricing) =>
                            cp.countryCode3 === countryCode.toUpperCase()
                        );
                        if (!countryPricing) return 0;
                        const rawPrice = parseFloat(
                          countryPricing.price
                            .toString()
                            .replace(/[^0-9.]/g, '')
                        );
                        const multiplier = countryPricing.multiplier || 100;
                        return multiplier === 1
                          ? rawPrice
                          : rawPrice / multiplier;
                      };

                      const priceA = getPrice(String(params1.id));
                      const priceB = getPrice(String(params2.id));

                      return priceA - priceB;
                    }
                  },
                  {
                    field: 'taxModel',
                    headerName: 'Tax model',
                    disableReorder: true,
                    flex: 1,
                    renderCell: (params: any) => {
                      return params.row.taxModel === TAX_TYPE.TAX_INCLUDED
                        ? 'Included'
                        : 'Excluded';
                    }
                  },
                  {
                    field: 'taxRate',
                    headerName: 'Tax rate',
                    disableReorder: true,
                    flex: 1,
                    renderCell: (params: any) => {
                      return params.row.taxRate / 100 + '%';
                    }
                  },
                  {
                    field: 'customPricingDeviation',
                    headerName: 'Exchange Rate Drift',
                    renderHeader: () => (
                      <Tooltip
                        arrow
                        title={
                          <AcTooltip content="Currency exchange rate change since update" />
                        }
                      >
                        <span style={{ fontWeight: '600' }}>
                          Exchange Rate Drift
                        </span>
                      </Tooltip>
                    ),
                    width: 200,
                    flex: 1,
                    renderCell: (params: GridRenderCellParams) => {
                      if (params.row.customPricingDeviation)
                        return (
                          <Stack
                            flexDirection="row"
                            gap={1}
                            alignItems="center"
                          >
                            {params.row.customPricingDeviation}
                            <Tooltip
                              arrow
                              title={
                                <AcTooltip content="Exchange rate drift anomaly" />
                              }
                            >
                              <ErrorOutlineOutlinedIcon
                                sx={{ color: '#A8A9B2' }}
                              />
                            </Tooltip>
                          </Stack>
                        );
                    }
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    disableReorder: true,
                    flex: 1,
                    renderCell: (params: any) => {
                      const cp = prices.find(
                        (cp: CustomPricing) =>
                          cp.countryCode3 === params.id.toUpperCase()
                      );
                      return (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            color:
                              (cp && cp.manuallyUpdated) ||
                              params.row.manuallyUpdated
                                ? 'green'
                                : 'black'
                          }}
                        >
                          {(cp && cp.manuallyUpdated) ||
                          params.row.manuallyUpdated
                            ? 'Manually Updated'
                            : 'Default'}
                        </Typography>
                      );
                    },
                    sortComparator: (a, b, params1, params2) => {
                      const cpA = prices.find(
                        (cp: CustomPricing) =>
                          cp.countryCode3 === String(params1.id).toUpperCase()
                      );
                      const cpB = prices.find(
                        (cp: CustomPricing) =>
                          cp.countryCode3 === String(params2.id).toUpperCase()
                      );

                      const manuallyUpdatedA = cpA?.manuallyUpdated || false;
                      const manuallyUpdatedB = cpB?.manuallyUpdated || false;

                      if (manuallyUpdatedA && !manuallyUpdatedB) {
                        return -1;
                      }
                      if (!manuallyUpdatedA && manuallyUpdatedB) {
                        return 1;
                      }
                      return 0;
                    }
                  }
                ]}
                rows={prices.filter((row: any) => row.countryCode3 !== 'USA')}
                columnVisibilityModel={{
                  customPricingDeviation: hasFeatureFlagPricePointAlerting
                }}
                loading={
                  getPricePoint.isFetching ||
                  calculatePricing.isLoading ||
                  isTyping
                }
                rowHeight={75}
              />
            </Box>
          </Stack>
        </AcCard>
      </Stack>
    </div>
  );
};

export default PricingForm;
